import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Target, MapPin, Star, Globe, Brain, Zap } from 'lucide-react';
import robotImage from '../assets/images/robot-header.png';
import handsLaptopImage from '../assets/images/hands-on-laptop.jpg';
import HeroSection from '../components/HeroSection';
import AnimatedImage from '../components/AnimatedImage';
import WaveOverlay from '../components/WaveOverlay';
import UnderConstructionModal from '../components/UnderConstructionModal';
import { Link } from 'react-router-dom';

const Home = ({ onBookMeeting }) => {
  const [showConstruction, setShowConstruction] = useState(false);

  const whatWeDo = [
    {
      icon: <MapPin className="w-5 h-5 text-orange-300" />,
      heading: 'GETTING FOUND',
      subheading: 'Show up when your customers are searching, on Google, Google Maps, AI search engines, and voice assistants.',
      items: [
        {
          title: 'SmartStart by Insight Digital Agency',
          body: [
            "Built for the small business owner who is ready to establish a professional online presence and willing to stay involved in maintaining it. SmartStart includes a 2-page custom website with original copy written for your specific business, plus a fully configured Google Business Profile and a clear roadmap so you know exactly how to keep it working. Hosting is included for the full term.",
            "$799 per year. Two-year minimum commitment. No setup fee.",
            "This is a strong foundation, built right from the start. What you do with it from there is up to you, and we will make sure you know how."
          ],
        },
        {
          title: 'Custom Websites and Web Solutions',
          body: [
            "Your website is working for you or against you right now. There is no neutral. We build and optimize sites that load fast, look right on every device, and turn visitors into customers rather than sending them to your competitor."
          ],
        },
        {
          title: 'Local Search, AI Search, and Voice Search',
          body: [
            "Being findable used to mean ranking on Google. It still does, but it also now means showing up in AI-generated answers, voice assistant responses, and location-based recommendations. We optimize for all of it, because that's where your customers are searching today. When your visibility is built correctly, it doesn't just bring traffic. It brings the right people, at the right moment, ready to buy."
          ],
        },
        {
          title: 'Google Business Profile Optimization',
          body: [
            "For most local businesses, your Google Business Profile is one of the strongest drivers of new business you have. When it's managed consistently and strategically, it becomes one of the hardest-working tools in your entire digital toolkit. We handle posts, photos, Q&A, and the ongoing refinements that keep you showing up and standing out."
          ],
        },
      ],
    },
    {
      icon: <Star className="w-5 h-5 text-orange-300" />,
      heading: 'BEING CHOSEN',
      subheading: 'Getting found is half the battle. This is the half that turns lookers into buyers.',
      items: [
        {
          title: 'Reputation Management',
          body: [
            "Customers decide whether to trust you before they ever contact you. We help you build a review profile that makes that decision easy, respond to feedback professionally, and ensure every public interaction reinforces the credibility you have worked to earn."
          ],
        },
        {
          title: 'AI Agents',
          body: [
            "Your best employee, available at every hour, never having an off day, always representing your business exactly the way you would want. We design, build, and continuously improve custom AI agents that handle customer conversations across chat, text, email, and voice. They book appointments, answer questions, and bring in a human when the situation calls for one. Your customers get immediate, professional responses. You get your time back."
          ],
        },
      ],
    },
    {
      icon: <Target className="w-5 h-5 text-orange-300" />,
      heading: 'RETAINING CUSTOMERS',
      subheading: 'The most profitable customer is the one you already have. Most businesses leave this revenue on the table entirely.',
      items: [
        {
          title: 'Email Marketing and Campaign Management',
          body: [
            "Consistent, strategic communication with your existing customers is one of the highest-return activities in your entire marketing mix. We build and manage campaigns that keep you top of mind, drive repeat business, and increase the lifetime value of every customer you've already earned."
          ],
        },
        {
          title: 'Social Media Strategy',
          body: [
            "Not a content treadmill. A sustainable presence built around what your specific customers actually respond to, designed to work for your business without consuming it."
          ],
        },
        {
          title: 'Workflow Automation and CRM Setup',
          body: [
            "Every hour spent on a manual task that could be automated is an hour not spent growing your business. We design custom systems that handle lead capture, follow-up, scheduling, and customer retention automatically, built around the tools that fit your operation."
          ],
        },
      ],
    },
    {
      icon: <Brain className="w-5 h-5 text-orange-300" />,
      heading: 'WORKING SMARTER',
      subheading: "Sometimes what you need doesn't fit a category. That's where we do some of our best work.",
      items: [
        {
          title: 'AI-Powered Content Creation',
          body: [
            "High-quality content across every format: blog posts, service pages, email sequences, social content, images, video, and voice. Created efficiently with AI tools and refined until it sounds exactly like you, not like a machine trying to sound like you."
          ],
        },
        {
          title: 'Custom Solutions',
          body: [
            "Mobile apps, integrations, sophisticated automations, and anything else that sits between where your business is and where it needs to go."
          ],
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Digital Marketing and AI Automation for Small Business | Insight Digital Agency</title>
        <meta name="description" content="Insight Digital Agency helps small and medium businesses grow with Local SEO, AI automation, reputation management, and workflow solutions. Get found online and save hours every week." />
        <link rel="canonical" href="https://insightdigitalagency.com/" />
        <meta property="og:title" content="Digital Marketing and AI Automation for Small Business | Insight Digital Agency" />
        <meta property="og:description" content="Insight Digital Agency helps small and medium businesses grow with Local SEO, AI automation, reputation management, and workflow solutions." />
        <meta property="og:url" content="https://insightdigitalagency.com/" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "ProfessionalService",
              "@id": "https://insightdigitalagency.com/#organization",
              "name": "Insight Digital Agency",
              "url": "https://insightdigitalagency.com",
              "logo": "https://insightdigitalagency.com/logo.png",
              "telephone": "+1-913-513-1525",
              "email": "info@insightdigitalagency.com",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Overland Park",
                "addressRegion": "KS",
                "addressCountry": "US"
              },
              "founder": {
                "@id": "https://insightdigitalagency.com/#karen-schwartz"
              },
              "sameAs": [
                "https://www.linkedin.com/company/insight-digital-agency",
                "https://www.facebook.com/insightdigitalagency"
              ]
            },
            {
              "@type": "Person",
              "@id": "https://insightdigitalagency.com/#karen-schwartz",
              "name": "Karen Schwartz",
              "alternateName": "KJ Schwartz",
              "jobTitle": "Founder & Strategic Architect",
              "worksFor": {
                "@id": "https://insightdigitalagency.com/#organization"
              },
              "sameAs": [
                "https://www.linkedin.com/in/karen-schwartz"
              ]
            }
          ]
        })}
        </script>
      </Helmet>
      <div className="min-h-screen">
      {/* Hero Section */}
      <HeroSection>
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-12 items-center">
          <div className="space-y-6 relative z-40">
            <p className="text-sm sm:text-base tracking-wide uppercase text-blue-100/90 font-semibold">
              Boutique Digital Agency. Midwest-Based. Built for Main Street America.
            </p>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              You Don't Need Another Agency. You Need Someone Who's Been Where You Are.
            </h1>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              We help small businesses get found online, turn visitors into customers, and run smarter operations. No jargon. No cookie-cutter packages. No long-term contracts.
            </p>
            <div className="space-y-4 text-blue-100 leading-relaxed text-base sm:text-lg">
              <p>
                You're juggling everything. Your website exists but you're not sure it works. Your Google listing is a mess, or worse, it's been suspended and nobody told you why. You're paying for tools you don't use. And every marketing expert in your feed wants to sell you the newest quick fix.
              </p>
              <p>
                It's not you. You're buried under tech overwhelm when all you want to do is run your business and serve your customers.
              </p>
              <p>
                Here's what's different about us: we've actually run small businesses. We know what it costs when the phone stops ringing, and we know exactly what it takes to make it ring again. If we don't think we can help you, we'll tell you to keep your money.
              </p>
            </div>
            <div className="pt-2 flex justify-center">
              <Button
                onClick={onBookMeeting}
                className="bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 relative z-50"
              >
                Book Your Free 30-Minute Conversation
              </Button>
            </div>
          </div>
          <div className="lg:translate-x-6">
            <AnimatedImage
              src={robotImage}
              alt="Robot holding tablet with analytics"
              type="robot"
            />
          </div>
        </div>
      </HeroSection>

      {/* Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-center">
            <div className="relative flex justify-center lg:justify-start items-center lg:-ml-40">
              {/* Large oval container bleeding off left side on desktop, centered on mobile */}
              <div className="relative overflow-hidden transform rotate-12 shadow-2xl
                            w-80 h-64 sm:w-96 sm:h-72 md:w-[500px] md:h-96 lg:w-[600px] lg:h-[450px] 
                            rounded-[50%] bg-gradient-to-br from-orange-100 to-blue-100">
                <img 
                  src={handsLaptopImage} 
                  alt="Hands working on laptop with digital marketing" 
                  className="absolute inset-0 w-full h-full object-cover object-center"
                />
                {/* Network overlay effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-transparent"></div>
                
                {/* Network connection dots */}
                <div className="absolute top-6 right-8 w-4 h-4 bg-white rounded-full shadow-lg animate-pulse"></div>
                <div className="absolute bottom-12 left-12 w-3 h-3 bg-orange-400 rounded-full shadow-lg animate-pulse" style={{animationDelay: '0.5s'}}></div>
                <div className="absolute top-1/3 left-6 w-3.5 h-3.5 bg-blue-400 rounded-full shadow-lg animate-pulse" style={{animationDelay: '1s'}}></div>
                <div className="absolute bottom-1/4 right-12 w-3 h-3 bg-white rounded-full shadow-lg animate-pulse" style={{animationDelay: '1.5s'}}></div>
                <div className="absolute top-1/2 right-6 w-2.5 h-2.5 bg-orange-300 rounded-full shadow-lg animate-pulse" style={{animationDelay: '2s'}}></div>
              </div>
            </div>
            <div className="space-y-6">
              <p className="text-sm tracking-wide uppercase text-orange-600 font-bold">
                WHO WE ARE
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 lg:whitespace-nowrap">
                We Built This Because We Were You.
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed text-base sm:text-lg">
                <p>
                  Between the two of us, we have 35 years of actually running small businesses. Not consulting for them. Not studying them. Running them, with real payroll, real customers, and real consequences when the marketing didn't work.
                </p>
                <p>
                  Karen spent years as a nurse, then built a career as a top-producing real estate broker before founding this agency. She understands online visibility the way a doctor understands the human body: not as a single thing to fix, but as a system where each component affects every other one. Get one piece wrong and the whole thing underperforms. Get them working together and the results compound to the positive.
                </p>
                <p>
                  Nikki is the builder. An AI tool architect, automation engineer, and professional voice talent, she designs and deploys the technical systems that turn strategy into measurable results.
                </p>
                <p>
                  We're based in the Midwest, Kansas and Missouri, and we work exclusively with small businesses. Not enterprise clients. Not venture-backed startups. Main Street America businesses that need a real partner, not a vendor who disappears after the invoice is marked paid.
                </p>
                <p>
                  Here's our promise: we don't overpromise, we don't ghost you, and we don't overcharge. We treat your business the way we'd want ours treated. And if we don't think we're the right fit for you, we'll tell you that before you spend a dime.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DO (replaces Personalized Digital Strategies + Boost Your Online Presence) */}
      <section className="py-32 hero-gradient text-white relative" style={{ paddingTop: '8rem' }}>
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 relative z-30">
          <div className="max-w-5xl mx-auto space-y-10 relative z-40">
            <div className="space-y-4">
              <p className="text-sm tracking-wide uppercase text-orange-300 font-bold">
                WHAT WE DO
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
                <span className="block">
                  The Right Solution at the Right Stage.
                </span>
                <span className="block">
                  Not a Package. Not a Guess.
                </span>
              </h2>
              <p className="text-blue-100 leading-relaxed text-base sm:text-lg">
                Every business we work with is at a different point in its growth. Some are invisible online and need to be found before anything else matters. Some are getting traffic but losing the sale to competitors with stronger credibility. Some are growing but drowning in manual work that should've been automated months ago. We start where you are, not where it's convenient for us to begin.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {whatWeDo.map((section) => (
                <div
                  key={section.heading}
                  className="group rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm hover:bg-white/12 transition-colors"
                >
                  <div className="p-6 sm:p-8 space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 w-9 h-9 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center">
                        <div className="transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-6 hover:scale-110 hover:-rotate-6">
                          {section.icon}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h3 className="text-lg sm:text-xl font-bold text-white tracking-wide">
                          {section.heading}
                        </h3>
                        <p className="text-blue-100 leading-relaxed">
                          {section.subheading}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {section.items.map((item) => (
                        <div key={item.title} className="rounded-xl bg-white/5 border border-white/12 p-5">
                          <h4 className="text-white font-semibold text-base sm:text-lg">
                            {item.title}
                          </h4>
                          <div className="mt-2 space-y-3 text-blue-100 leading-relaxed">
                            {item.body.map((paragraph) => (
                              <p key={paragraph}>{paragraph}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-sm p-6 sm:p-8 space-y-4">
              <h3 className="text-lg sm:text-xl font-bold text-orange-200">
                NOT SURE WHERE TO START?
              </h3>
              <div className="space-y-3 text-blue-100 leading-relaxed text-base sm:text-lg">
                <p>
                  That's exactly what the free conversation is for. We take a thorough look at your business, your goals, and where the real gaps are before we recommend anything. You'll leave with a clear picture and a straight answer.
                </p>
                <p>
                  Sometimes that means a defined path forward together. Sometimes it means a direct conversation about whether the timing is right, whether your budget can support what actually needs to be in place, or whether we're the right fit for each other at this point in time. We are selective about who we work with, because the engagements that deliver real results are the ones where both sides fully understand the value being exchanged.
                </p>
                <p>
                  Either way, you will know exactly where you stand.
                </p>
              </div>
              <div className="pt-2 flex justify-center">
                <Button
                  onClick={onBookMeeting}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold rounded-lg transition-all duration-300"
                >
                  Book Your Free 30-Minute Conversation
                </Button>
              </div>
            </div>
          </div>
        </div>
        <WaveOverlay />
      </section>

      {/* About Section */}
      <section className="py-32 hero-gradient text-white relative">
        <WaveOverlay inverted={true} color="fill-white" />
        <div className="container mx-auto px-4 relative z-30">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold">
              <span className="block lg:whitespace-nowrap">
                Two People. Thirty-Five Years of Real-World Experience.
              </span>
              <span className="block text-center">
                One Focus.
              </span>
            </h2>
            <p className="text-lg sm:text-xl text-blue-100 leading-relaxed">
              We didn't come from marketing. We came from business. That difference shapes everything about how we work, what we recommend, and what we refuse to sell you. If you want to know the full story, including where we've been and why we built this agency, it's worth a few minutes of your time.
            </p>
            <div className="pt-4 flex justify-center">
              <Link to="/about">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-300 transform hover:scale-105">
                  Meet the Team
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <WaveOverlay />
      </section>

      {/* Insights Section */}
      <section className="py-20 bg-white relative">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-6">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              Real Answers for Real Business Owners.
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              No fluff, no filler. Our blog covers the specific questions small business owners are actually asking: why your Google listing isn't showing up, what AI search means for your visibility, how to know if your website is working against you, and what to do about it. Straight answers, plain language, no agenda.
            </p>
            <div className="relative inline-block">
              <Button 
                onClick={() => setShowConstruction(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-lg"
              >
                Read Our Blogs
              </Button>
              
              {/* Under Construction Animation */}
              <UnderConstructionModal 
                isOpen={showConstruction} 
                onClose={() => setShowConstruction(false)} 
              />
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Home;

